'use client';

import { TUserType } from '@/types';
import { createContext, useContext, useState, ReactNode } from 'react';

type TFormType = 'login' | 'register';

interface AuthModalContextType {
  isOpen: boolean;
  formType: TFormType;
  userType: TUserType;
  openModal: (initialFormType?: TFormType, initialUserType?: TUserType) => void;
  closeModal: () => void;
  switchForm: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export const AuthModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formType, setFormType] = useState<TFormType>('login');
  const [userType, setUserType] = useState<TUserType>('visitor');

  const openModal = (initialFormType: TFormType = 'login', initialUserType: TUserType = 'visitor') => {
    setFormType(initialFormType);
    setUserType(initialUserType);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const switchForm = () => {
    setFormType((prevForm) => (prevForm === 'login' ? 'register' : 'login'));
  };

  return (
    <AuthModalContext.Provider
      value={{
        isOpen,
        formType,
        userType,
        openModal,
        closeModal,
        switchForm,
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
};
